/**
 * 1.6.0 AI suggestions: the data hooks (docs/dev/mcp-1.6.0-build.md). Step 0 wrote them against A's routes (the stubs
 * list no metadata suggestions; the actions answer 501); stream A owns this file now.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AcceptAllResult, MetadataSuggestionDTO, SegmentDTO } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

const segmentsChanged = (qc: ReturnType<typeof useQueryClient>, assetId: string) => { void qc.invalidateQueries({ queryKey: queryKeys.segments(assetId) }); };

export function useAcceptSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, segmentId }: { assetId: string; segmentId: string }) => api<SegmentDTO>(`/api/v1/assets/${assetId}/segments/${segmentId}/accept`, { method: 'POST', json: {} }),
    onSuccess: (_d, v) => segmentsChanged(qc, v.assetId),
  });
}

export function useRejectSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, segmentId }: { assetId: string; segmentId: string }) => api(`/api/v1/assets/${assetId}/segments/${segmentId}/reject`, { method: 'POST', json: {} }),
    onSuccess: (_d, v) => segmentsChanged(qc, v.assetId),
  });
}

export function useAcceptAllSegments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, stratumId }: { assetId: string; stratumId: string }) => api<AcceptAllResult>(`/api/v1/assets/${assetId}/segments/accept-all`, { method: 'POST', json: { stratumId } }),
    onSuccess: (_d, v) => segmentsChanged(qc, v.assetId),
  });
}

export function useMetadataSuggestions(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.metadataSuggestions(assetId),
    queryFn: () => api<{ items: MetadataSuggestionDTO[] }>(`/api/v1/assets/${assetId}/metadata-suggestions`),
    enabled: Boolean(assetId),
  });
}

export function useDecideMetadataSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, id, decision }: { assetId: string; id: string; decision: 'accept' | 'reject' }) =>
      api(`/api/v1/assets/${assetId}/metadata-suggestions/${id}/${decision}`, { method: 'POST', json: {} }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: queryKeys.metadataSuggestions(v.assetId) });
      void qc.invalidateQueries({ queryKey: queryKeys.asset(v.assetId) });
    },
  });
}
